import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Search, X, Tv, Wind, UtensilsCrossed, Fan, Speaker, ArrowRight } from 'lucide-react';
import { getProducts, getFeaturedProducts, getCategories, getSearchConfig, getSearchRecommendations } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';

const PremiumSearch = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [searchConfig, setSearchConfig] = useState({
    heroImage: 'https://images.unsplash.com/photo-1593642632823-8f785ba67e45',
    headline: 'Descubre lo extraordinario',
    subtitle: 'Calidad y estilo para cada espacio'
  });
  const [isSearching, setIsSearching] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [config, featured, cats, recs] = await Promise.all([
          getSearchConfig(),
          getFeaturedProducts(),
          getCategories(),
          getSearchRecommendations()
        ]);
        
        setSearchConfig(config);
        setFeaturedProducts(featured.slice(0, 3));
        setCategories(cats.filter(c => c.id !== 'todos'));
        setRecommendations(recs);
      } catch (error) {
        console.error('Error loading search data:', error);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Search products in real-time
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.length === 0) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      if (searchQuery.length < 2) return;

      setIsSearching(true);

      try {
        const allProducts = await getProducts();
        const query = searchQuery.toLowerCase();
        
        const results = allProducts
          .filter(p => 
            p.available !== false && (
              p.name.toLowerCase().includes(query) ||
              p.brand.toLowerCase().includes(query) ||
              p.category.toLowerCase().includes(query) ||
              p.description.toLowerCase().includes(query)
            )
          )
          .slice(0, 5);
        
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    onClose();
    setSearchQuery('');
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
    onClose();
  };

  const handleRecommendationClick = (destination) => {
    // Support both internal routes and external URLs
    if (destination.startsWith('http://') || destination.startsWith('https://')) {
      window.open(destination, '_blank');
    } else {
      navigate(destination);
    }
    onClose();
  };

  const iconMap = {
    Tv: Tv,
    Wind: Wind,
    UtensilsCrossed: UtensilsCrossed,
    Fan: Fan,
    Speaker: Speaker
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 bg-transparent border-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-md flex items-center justify-center transition-all text-white"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Hero Section */}
          <div className="relative h-[300px] overflow-hidden rounded-t-2xl">
            <img
              src={searchConfig.heroImage}
              alt="Search Hero"
              className="w-full h-full object-cover"
            />
            {/* Subtle overlay - not too dark */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
            
            {/* Hero Text - Apple Style Typography */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-7xl text-white mb-4"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
                  fontWeight: 400,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  color: '#F5F5F7'
                }}
              >
                {(() => {
                  const headline = typeof searchConfig.headline === 'string' 
                    ? searchConfig.headline 
                    : searchConfig.headline?.es || 'Descubre lo extraordinario';
                  const words = headline.split(' ');
                  return (
                    <>
                      {words.slice(0, 1).join(' ')}{' '}
                      <span style={{ fontWeight: 600 }}>
                        {words.slice(1).join(' ')}
                      </span>
                    </>
                  );
                })()}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="text-xl md:text-2xl mt-2"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.4,
                  color: '#D1D1D6',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                {typeof searchConfig.subtitle === 'string' 
                  ? searchConfig.subtitle 
                  : searchConfig.subtitle?.es || 'Calidad y estilo para cada espacio'}
              </motion.p>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-background rounded-b-2xl">
            {/* Search Bar */}
            <div className="px-8 py-8">
              <div className="relative max-w-3xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar productos..."
                  className="pl-12 pr-4 h-14 text-lg border-2 focus:border-primary rounded-full bg-muted/50"
                  autoFocus
                />
              </div>
            </div>

            {/* Search Results */}
            <AnimatePresence>
              {searchQuery.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-8 pb-6 overflow-hidden"
                >
                  <div className="max-w-3xl mx-auto space-y-2">
                    {isSearching ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Buscando...
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((product) => (
                        <motion.div
                          key={product.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          onClick={() => handleProductClick(product.id)}
                          className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/50 cursor-pointer transition-all group"
                        >
                          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-lg p-2">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base truncate group-hover:text-primary transition-colors">
                              {product.name}
                            </h3>
                            <Badge variant="secondary" className="text-xs mt-1">
                              {product.category}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">
                              RD${product.price.toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Desde RD${product.monthly_payment.toLocaleString()}/mes
                            </p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No se encontraron productos
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Categories & Featured Products - Only show when NOT searching */}
            {searchQuery.length === 0 && (
              <div className="px-8 pb-8 space-y-8">
                {/* Search Recommendations */}
                {recommendations.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
                      Recomendaciones
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {recommendations.map((rec) => (
                        <button
                          key={rec.id}
                          onClick={() => handleRecommendationClick(rec.destination)}
                          className="flex flex-col items-center justify-center p-4 rounded-xl bg-muted/50 hover:bg-muted transition-all group relative overflow-hidden"
                        >
                          {/* Background Image */}
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity">
                            <img
                              src={rec.imageUrl}
                              alt={rec.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {/* Icon/Image */}
                          <div className="w-12 h-12 mb-2 rounded-lg overflow-hidden bg-background relative z-10">
                            <img
                              src={rec.imageUrl}
                              alt={rec.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                            />
                          </div>
                          
                          <span className="text-sm font-medium text-center relative z-10">
                            {rec.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fallback to Categories if no recommendations */}
                {recommendations.length === 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
                      Explorar por categoría
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {categories.map((category) => {
                        const IconComponent = iconMap[category.icon];
                        return (
                          <button
                            key={category.id}
                            onClick={() => handleCategoryClick(category.id)}
                            className="flex flex-col items-center justify-center p-6 rounded-xl bg-muted/50 hover:bg-muted transition-all group"
                          >
                            {IconComponent && (
                              <IconComponent className="h-8 w-8 mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
                            )}
                            <span className="text-sm font-medium text-center">
                              {category.name.es}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Featured Products */}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
                    Productos destacados
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {featuredProducts.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductClick(product.id)}
                        className="group cursor-pointer rounded-xl overflow-hidden bg-muted/30 hover:bg-muted/50 transition-all"
                      >
                        <div className="aspect-square bg-white p-4">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-semibold text-sm line-clamp-2 mb-2">
                            {product.name}
                          </h4>
                          <p className="text-lg font-bold">
                            RD${product.price.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumSearch;
